// PTT-vaktlista: én kanal å SENDE på, flere å LYTTE på.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// EIERKRAV 2026-08-18: «så kan man sette opp Dualwatch og Triwatch, men at det er
// enkelt å bytte mellom TX i de gruppene du er med i, og at RX er på flere.»
// Pluss: «All Call» — SKADE skal kunne tvinge GNÅ-brukere inn på en kanal de må
// lytte på, «spesielt for å sikre at alle er stilt på riktig kanal».
//
// Dette er radiobrukerens forventning, og den er riktig. Men den bærer fire
// egenskaper som må ligge i TYPENE, ikke i et UI som husker å gjøre det rett:
//
// ⚠ 1. DET ER ALLTID NØYAKTIG ÉN TX-KANAL.
//   Ikke fordi flere er teknisk umulig, men fordi tvetydig TX er lettalt: du
//   trykker en fysisk knapp under stress og snakker, og det går til et annet nett
//   enn du trodde. I ildstøtte er det forskjellen på en korreksjon og en granat i
//   egne rekker. `settTx` FLYTTER senderetten; den kan ikke legge til en ny.
//
// ⚠ 2. DUALWATCH SPILLER IKKE TO SENDINGER SAMTIDIG.
//   Tre nett i ørene samtidig er uforståelig. Radio løser det med prioritet: det
//   høyeste aktive nettet demper de andre. `hvaHoresNaa` gir derfor ÉN hørbar
//   sending — og LISTER de som ble fortrengt.
//
// ⚠ 3. EN FORTRENGT SENDING ER TAPT TRAFIKK, OG SKAL MERKES SOM DET.
//   Dette er den farligste delen av Dualwatch. På en radio HØRER du at to
//   snakker samtidig; digitalt forsvinner den svakeste i stillhet. Med opptak
//   (eierbeslutning B-005: lyd på disk = ja) kan den fortrengte sendingen spilles
//   av etterpå — men bare hvis noen har ført den. Derfor returnerer
//   `hvaHoresNaa` `fortrengt`, og kalleren SKAL varsle på den.
//
// ⚠ 4. EN PÅTVUNGET KANAL MÅ VÆRE SYNLIG, OG KAN IKKE ÅPNE MIKROFONEN.
//   «All Call» er en legitim kommandohandling — det digitale «alle stasjoner, gå
//   til nett 2». Men to grenser er absolutte: den kan bare tvinge RX (aldri TX,
//   og ALDRI mikrofonen — det ville vært skjult avlytting, som eier har avvist),
//   og soldaten SKAL se at han er flyttet, av hvem. En ufrivillig endring brukeren
//   ikke kan se er umulig å skille fra en feil, og ødelegger tilliten til
//   systemet raskere enn den løser problemet den var ment å løse.
import { z } from 'zod';
/** Rollen en kanal har i vaktlista. */
export const VaktrolleSchema = z.enum(['tx', 'rx']);
/**
 * Hvor mange kanaler kan lyttes på samtidig.
 *
 * ⚠ TRE ER EN DOKTRINEGRENSE, IKKE EN TEKNISK. Fire nett å holde rede på er ikke
 * overvåking, det er støy — og operatøren slutter å høre alle fire. Dualwatch (2)
 * og Triwatch (3) er de begrepene radiobrukeren har, og de er der fordi de er
 * det et menneske klarer.
 */
export const MAKS_RX = 3;
export const KanalvaktSchema = z.object({
    kanalId: z.string().min(1).max(300),
    /** Navn slik operatøren leser det. */
    navn: z.string().min(1).max(200),
    rolle: VaktrolleSchema,
    /**
     * Prioritet. LAVERE tall = høyere prioritet (som radio-scanlister).
     * 0 er reservert nødkanal.
     */
    prioritet: z.number().int().min(0).max(99).default(50),
    /**
     * Satt av en kommandohandling («All Call») og kan ikke fjernes av brukeren.
     * Bærer HVEM som påla den — en påtvunget kanal uten avsender er umulig å skille
     * fra en feil.
     */
    paatvungetAv: z.string().max(200).optional(),
    /** Er dette nødkanalen? Kan aldri fjernes eller dempes. */
    nod: z.boolean().default(false),
});
/**
 * Flytt senderetten til en kanal.
 *
 * ⚠ FLYTTER, LEGGER IKKE TIL. Den gamle TX blir RX (du hører fortsatt nettet du
 * forlot — det er nettopp poenget med Dualwatch), og det finnes aldri to TX.
 */
export function settTx(liste, kanalId) {
    const mål = liste.find((k) => k.kanalId === kanalId);
    if (!mål) {
        return {
            liste: [...liste], avvist: true,
            melding: 'Kanalen er ikke i vaktlista. Legg den til før du kan sende på den.',
        };
    }
    if (mål.rolle === 'tx') {
        return { liste: [...liste], avvist: false, melding: `Sender allerede på ${mål.navn}.` };
    }
    const forrige = liste.find((k) => k.rolle === 'tx');
    const ny = liste.map((k) => ({
        ...k,
        rolle: (k.kanalId === kanalId ? 'tx' : 'rx'),
    }));
    return {
        liste: ny, avvist: false,
        melding: forrige
            ? `Sender nå på ${mål.navn}. Lytter fortsatt på ${forrige.navn}.`
            : `Sender nå på ${mål.navn}.`,
    };
}
/** Kanalen senderetten står på, eller `null`. */
export function txKanal(liste) {
    return liste.find((k) => k.rolle === 'tx') ?? null;
}
/**
 * Legg en kanal i vaktlista.
 *
 * ⚠ AVVISER OVER `MAKS_RX` MED EN FORKLARING, framfor å kaste ut den eldste. Å
 * fjerne et nett operatøren tror hun lytter på er verre enn å nekte et nytt: hun
 * ville sittet og ventet på trafikk som aldri kom.
 */
export function leggTilVakt(liste, vakt) {
    if (liste.some((k) => k.kanalId === vakt.kanalId)) {
        return { liste: [...liste], avvist: false, melding: `${vakt.navn} er allerede i vaktlista.` };
    }
    // Nødkanalen kommer alltid inn, uansett hvor full lista er.
    if (!vakt.nod && liste.length >= MAKS_RX) {
        return {
            liste: [...liste], avvist: true,
            melding: `Vaktlista er full (${MAKS_RX} kanaler). Fjern en før du legger til `
                + `${vakt.navn} — fire nett å holde rede på er ikke overvåking, det er støy.`,
        };
    }
    // Første kanal får senderetten; ellers kommer den inn som RX.
    const rolle = liste.length === 0 ? 'tx' : 'rx';
    return {
        liste: [...liste, { ...vakt, rolle }], avvist: false,
        melding: rolle === 'tx' ? `${vakt.navn} lagt til — du sender på den.` : `${vakt.navn} lagt til (lytter).`,
    };
}
/**
 * Fjern en kanal fra vaktlista.
 *
 * ⚠ TRE TING KAN IKKE FJERNES, OG ALLE TRE ER SIKKERHET:
 *  • nødkanalen — den er ikke operatørens valg,
 *  • en påtvungen kanal («All Call») — da var ordren meningsløs,
 *  • den siste kanalen, hvis den er TX — et samband uten senderett er ikke et
 *    samband, og en operatør som ikke merker at hun ikke kan sende er farligere
 *    enn en som vet hun er stum.
 */
export function fjernVakt(liste, kanalId) {
    const mål = liste.find((k) => k.kanalId === kanalId);
    if (!mål)
        return { liste: [...liste], avvist: false, melding: 'Kanalen var ikke i vaktlista.' };
    if (mål.nod) {
        return {
            liste: [...liste], avvist: true,
            melding: 'Nødkanalen kan ikke fjernes fra vaktlista.',
        };
    }
    if (mål.paatvungetAv) {
        return {
            liste: [...liste], avvist: true,
            melding: `${mål.navn} er pålagt av ${mål.paatvungetAv} og kan ikke fjernes. `
                + 'Ta det opp med den som satte den.',
        };
    }
    const rest = liste.filter((k) => k.kanalId !== kanalId);
    if (!rest.length) {
        return {
            liste: [...liste], avvist: true,
            melding: 'Kan ikke fjerne den siste kanalen — du ville stått uten samband.',
        };
    }
    // Mistet vi senderetten, må den flyttes — ellers står operatøren stum uten å
    // vite det.
    if (mål.rolle === 'tx') {
        const ny = settTx(rest, [...rest].sort((a, b) => a.prioritet - b.prioritet)[0].kanalId);
        return {
            liste: ny.liste, avvist: false,
            melding: `${mål.navn} fjernet. ${ny.melding}`,
        };
    }
    return { liste: rest, avvist: false, melding: `${mål.navn} fjernet.` };
}
// ── All Call: påtvungen kanal fra SKADE ──────────────────────────────────────
export const AllCallSchema = z.object({
    kanalId: z.string().min(1).max(300),
    navn: z.string().min(1).max(200),
    /** Hvem som påla den. Stemples server-side. */
    paalagtAv: z.string().min(1).max(200),
    paalagtMs: z.number().int(),
    grunn: z.string().max(500).default(''),
    /** Skal senderetten også flyttes hit? */
    ogsaaTx: z.boolean().default(false),
    prioritet: z.number().int().min(0).max(99).default(10),
});
/**
 * Legg en påtvungen kanal inn i en vaktliste.
 *
 * ⚠ DENNE FUNKSJONEN KAN IKKE ÅPNE MIKROFONEN, OG DET ER MED VILJE STRUKTURELT.
 * Den setter `rolle` til `rx` (eller `tx` når ordren sier det), og den har ingen
 * vei til opptak eller sending uten operatørens knapp. Skjult avlytting er
 * avvist av eier (2026-08-18), og skillet skal ligge i typen — ikke i at ingen
 * har kommet på å misbruke den.
 *
 * ⚠ FORTRENGER OM NØDVENDIG, MEN ALDRI NØDKANALEN. Er lista full, må noe vike;
 * det som vikes er kanalen med lavest prioritet som IKKE er nød og IKKE er
 * påtvungen av noen annen.
 */
export function paatvingKanal(liste, ordre) {
    const finnes = liste.find((k) => k.kanalId === ordre.kanalId);
    let arbeid = [...liste];
    if (!finnes && arbeid.length >= MAKS_RX) {
        const kandidater = arbeid
            .filter((k) => !k.nod && !k.paatvungetAv)
            .sort((a, b) => b.prioritet - a.prioritet);
        if (!kandidater.length) {
            return {
                liste: arbeid, avvist: true,
                melding: `Kan ikke pålegge ${ordre.navn}: vaktlista er full av nødkanal og `
                    + 'andre pålagte kanaler.',
            };
        }
        arbeid = arbeid.filter((k) => k.kanalId !== kandidater[0].kanalId);
    }
    const vakt = KanalvaktSchema.parse({
        kanalId: ordre.kanalId,
        navn: ordre.navn,
        rolle: ordre.ogsaaTx ? 'tx' : 'rx',
        prioritet: ordre.prioritet,
        paatvungetAv: ordre.paalagtAv,
        nod: false,
    });
    arbeid = finnes
        ? arbeid.map((k) => (k.kanalId === ordre.kanalId ? { ...k, ...vakt } : k))
        : [...arbeid, vakt];
    // Bare ÉN TX: flytter ordren senderetten, må de andre bli RX.
    if (ordre.ogsaaTx) {
        arbeid = arbeid.map((k) => ({
            ...k, rolle: (k.kanalId === ordre.kanalId ? 'tx' : 'rx'),
        }));
    }
    return {
        liste: arbeid, avvist: false,
        // ⚠ MELDINGA ER IKKE VALGFRI. En ufrivillig kanalendring brukeren ikke ser er
        // umulig å skille fra en feil.
        melding: `${ordre.paalagtAv} har satt deg på ${ordre.navn}`
            + `${ordre.ogsaaTx ? ' (også senderett)' : ' (lytting)'}`
            + `${ordre.grunn ? `: ${ordre.grunn}` : '.'}`,
    };
}
/**
 * Hvilken sending er hørbar nå?
 *
 * ⚠ NØD SLÅR ALT, UANSETT VAKTLISTE OG PRIORITET. En nødsending fra en kanal du
 * lytter på skal bryte gjennom, og en nødkanal kan ikke dempes.
 * ⚠ FLASH SLÅR RESTEN. Presedens er hele grunnen til at `precedence.ts` finnes.
 * Deretter avgjør kanalprioritet, og til slutt den som begynte først — en sending
 * som allerede er i gang skal ikke hakkes opp av en som starter samtidig.
 */
export function hvaHoresNaa(liste, aktive) {
    const iVakt = aktive.filter((a) => liste.some((k) => k.kanalId === a.kanalId));
    if (!iVakt.length)
        return { spiller: null, fortrengt: [], grunn: 'Stille på alle kanaler i vaktlista.' };
    const vaktFor = new Map(liste.map((k) => [k.kanalId, k]));
    const rang = (a) => {
        const v = vaktFor.get(a.kanalId);
        if (v?.nod)
            return -1000; // nødkanal slår alt
        if (a.presedens === 'flash')
            return -100; // FLASH slår resten
        return v?.prioritet ?? 99;
    };
    const sortert = [...iVakt].sort((a, b) => {
        const r = rang(a) - rang(b);
        if (r !== 0)
            return r;
        return a.startMs - b.startMs; // den som begynte først beholder ordet
    });
    const spiller = sortert[0];
    const fortrengt = sortert.slice(1);
    const v = vaktFor.get(spiller.kanalId);
    const grunn = v?.nod
        ? 'NØDKANAL — bryter gjennom alt.'
        : spiller.presedens === 'flash'
            ? 'FLASH — bryter gjennom.'
            : fortrengt.length
                ? `${v?.navn ?? spiller.kanalId} har høyeste prioritet nå.`
                : `${v?.navn ?? spiller.kanalId}.`;
    return { spiller, fortrengt, grunn };
}
/**
 * Kan operatøren sende akkurat nå?
 *
 * ⚠ HALV DUPLEKS: å sende mens noen andre snakker på SAMME kanal er en kollisjon,
 * og på radio hører begge det. Digitalt gjør de ikke det, så vi sier fra.
 */
export function kanSende(liste, aktive, meg) {
    const tx = txKanal(liste);
    if (!tx)
        return { kan: false, grunn: 'Ingen kanal har senderett. Velg en kanal å sende på.' };
    const opptatt = aktive.find((a) => a.kanalId === tx.kanalId && a.fra !== meg);
    if (opptatt) {
        return { kan: false, grunn: `${opptatt.fra} snakker på ${tx.navn}. Vent til kanalen er ledig.` };
    }
    return { kan: true, grunn: `Klar til å sende på ${tx.navn}.` };
}
/** Kort status til vaktlinja: «TX: Tropp 2 · RX: Distrikt, NØD». */
export function vaktlinje(liste) {
    const tx = txKanal(liste);
    const rx = liste.filter((k) => k.rolle === 'rx');
    const deler = [];
    // ⚠ TX FØRST OG ALLTID. Det operatøren MÅ vite før hun trykker er hvor det går.
    deler.push(tx ? `TX: ${tx.navn}` : '⚠ TX: ingen');
    if (rx.length)
        deler.push(`RX: ${rx.map((k) => k.navn).join(', ')}`);
    return deler.join(' · ');
}
//# sourceMappingURL=pttvakt.js.map